/*
 * <recs-engine> — optional Algolia Recommend fill for Featured Collection.
 *
 * When data-engine is "algolia:<model>", fetches recommendations and replaces
 * [data-recs-grid] children by cloning <template data-recs-card>.
 * Collection (or placeholder) cards already in the grid stay put if Algolia
 * errors, returns nothing, or is missing config/seeds.
 *
 * data-fallback-engine (optional) is tried next — used so Frequently bought
 * together can fall back to Related items before the collection.
 *
 * Models: bought-together | related-products | looking-similar | trending-items
 *
 * ═════ RS 2026-09-09 — cuatro síntomas, una sola causa ═════
 * Abi reportó que en el shelf de la PDP las flechas no hacían nada, no había
 * corazón de wishlist y no había quick add. Los tres salían de acá: este
 * archivo REEMPLAZA las tarjetas que renderizó Liquid — completas — por clones
 * de un <template> mucho más pobre, y lo hace DESPUÉS de que el resto del tema
 * ya se inicializó.
 *
 * Por eso el mismo carrusel se comportaba distinto según la carga: si Algolia
 * contestaba, las tarjetas perdían cosas; si no contestaba, quedaban las de
 * Liquid y estaba todo. En la home no pasa nunca, porque ahí la fuente es una
 * colección y este componente ni existe.
 *
 *   1. <carousel-slider> se da de baja si arranca vacío (queda `inactive`).
 *      #refreshCarousel solo refrescaba; ahora inicializa si hace falta.
 *   2. El template no tenía corazón de boards. Ahora se CLONA el picker que
 *      el propio tema ya renderizó en la grilla — ver #capturePicker.
 *   3. El template no tenía quick add. Idem — ver #captureQuickAdd. Estaba
 *      documentado como decisión deliberada; Abi la revirtió el 2026-09-09
 *      porque dejaba a la misma sección comportándose distinto según la
 *      página. La atribución que dependía de esa decisión se rehizo: ver
 *      el comentario sobre trackRecsClick (la conversión la envía
 *      assets/algolia-insights.js, único escritor desde 2026-09-18).
 *   4. Si el fetch a Algolia no contestaba nunca, `aria-busy` se quedaba en
 *      "true" para siempre y un lector de pantalla anunciaba la grilla como
 *      "cargando" sin fin. Ahora hay timeout y el estado se limpia en finally.
 *
 * >>> POR QUÉ SE CLONA Y NO SE ESCRIBE EL MARKUP ACÁ, para los dos casos: el
 * popover del picker son ~30 nodos y el quick add depende de clases del tema.
 * Medido: un .rs-bp con solo el botón no abre nada — rs-boards.js NO construye
 * el popover. Clonar desde la grilla deja UNA sola fuente de verdad: si mañana
 * cambian rs-board-picker.liquid o product-card.liquid, la tarjeta de
 * recomendación lo hereda sola.
 *
 * Y los listeners de los dos son DELEGADOS — verificado: el corazón clonado
 * abre, y quick-add.js escucha `click` en document y filtra por
 * .matches('.js-quick-add'). Así que alcanza con reescribir atributos.
 */
(function (root) {
  const EXCLUDED_HANDLE_PARTS = [
    'residence-supply-membership',
    'warranty',
    '100-damage-protection'
  ];
  // Kept in step with assets/algolia-instantsearch.js.liquid and
  // assets/algolia-predictive-search.js.liquid — search and recommendations
  // must hide the same products.
  const SEARCH_EXCLUDED_TAGS = ['tag_module_item', 'add-on', '100% damage protection', 'membership'];
  const SEARCH_EXCLUDED_TITLES = ['customize product', 'add-on', '100% damage protection', 'membership'];

  // Carousel-only: an Open Box unit is a clearance duplicate of a product the
  // shopper is already looking at, so it never belongs in a cross-sell.
  const EXCLUDED_TAGS = SEARCH_EXCLUDED_TAGS.concat(['open box']);
  const EXCLUDED_TITLE_PATTERNS = SEARCH_EXCLUDED_TITLES.concat(['open box']);

  // Un fetch sin timeout deja aria-busy en "true" para siempre — medido.
  const FETCH_TIMEOUT_MS = 8000;

  function parseJsonAttr(raw) {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /** Mirrors keepProduct() in algolia-predictive-search.js.liquid. */
  function hitTags(hit) {
    const tags = (hit && (hit.tags || hit._tags)) || [];
    const list = typeof tags === 'string' ? tags.split(',') : tags;
    return (Array.isArray(list) ? list : []).map((tag) => String(tag).trim().toLowerCase());
  }

  function isExcluded(hit, excludedHandles) {
    const handle = String(hit.handle || '').toLowerCase();
    if (!handle) return true;
    if (excludedHandles.has(handle)) return true;
    for (const part of EXCLUDED_HANDLE_PARTS) {
      if (handle.includes(part)) return true;
    }

    // Second line of defence behind the index filters: a Recommend model can
    // still surface a module item if the record's tags are stale.
    for (const tag of hitTags(hit)) {
      if (EXCLUDED_TAGS.includes(tag)) return true;
    }

    const title = String(hit.title || '').toLowerCase();
    for (const pattern of EXCLUDED_TITLE_PATTERNS) {
      if (title.includes(pattern)) return true;
    }

    // Configurator products index at 0 — the card design leads with "From $X",
    // so a hit we cannot price is a broken card.
    if (!(parseFloat(hit.variants_min_price) > 0)) return true;
    return false;
  }

  function mergeHits(results, excludeHandles) {
    const excluded = new Set((excludeHandles || []).map((h) => String(h).toLowerCase()));
    const seen = new Set();
    const merged = [];
    const maxLength = Math.max(0, ...(results || []).map((r) => (r.hits || []).length));

    for (let i = 0; i < maxLength; i += 1) {
      for (const result of results || []) {
        const hit = (result.hits || [])[i];
        if (!hit || !hit.handle || seen.has(hit.handle)) continue;
        seen.add(hit.handle);
        if (isExcluded(hit, excluded)) continue;
        merged.push(hit);
      }
    }

    return merged;
  }

  function modelChain(engine, fallbackEngine) {
    const chain = [];
    for (const value of [engine, fallbackEngine]) {
      if (!String(value || '').startsWith('algolia:')) continue;
      const model = String(value).split(':')[1];
      if (model && !chain.includes(model)) chain.push(model);
    }
    return chain;
  }

  /**
   * Buckets the flat Algolia results back onto the model that asked for them,
   * stamping each hit with the queryID of the request that produced it.
   * Recommend returns a queryID per result, and Algolia ignores click and
   * conversion events that arrive without one, so it has to survive the merge.
   */
  function groupResultsByModel(results, requestModel, models) {
    const byModel = new Map(models.map((model) => [model, []]));
    (results || []).forEach((result, index) => {
      const model = requestModel[index];
      const group = byModel.get(model);
      if (!group) return;
      for (const hit of result.hits || []) {
        hit.__queryID = result.queryID || '';
        hit.__model = model;
      }
      group.push(result);
    });
    return models.map((model) => ({ model, results: byModel.get(model) || [] }));
  }

  /** Takes from each model in chain order until the row is full. */
  function fillFromModels(groups, excludeHandles, limit) {
    const taken = (excludeHandles || []).map((handle) => String(handle).toLowerCase());
    const hits = [];
    const models = [];

    for (const group of groups || []) {
      if (hits.length >= limit) break;
      const available = mergeHits(group.results, taken);
      if (available.length === 0) continue;

      models.push(group.model);
      for (const hit of available) {
        if (hits.length >= limit) break;
        hits.push(hit);
        taken.push(String(hit.handle).toLowerCase());
      }
    }

    return { hits, models };
  }

  // Unlike search, recommendations do not filter on stock — a back-in-stock
  // or made-to-order piece is still worth showing next to what you are viewing.
  function buildFilters(excludeProductIds) {
    // Merchant tags live on `tags`. `_tags` is Algolia's internal
    // id/product/variant markers — filtering on it excludes nothing.
    const parts = [];
    for (const tag of EXCLUDED_TAGS) {
      parts.push(`NOT tags:"${tag}"`);
    }
    // The numeric product `id` is the only identifier this index honours:
    // `handle` is not faceted and `NOT objectID:` is ignored. Handles are still
    // excluded client-side in isExcluded().
    for (const id of (excludeProductIds || []).slice(0, 20)) {
      const numeric = parseInt(id, 10);
      if (!Number.isNaN(numeric)) parts.push(`NOT id=${numeric}`);
    }
    return parts.join(' AND ');
  }

  function formatPrice(hit, currency, lang) {
    const min = parseFloat(hit && hit.variants_min_price);
    if (Number.isNaN(min)) return '';

    const formatter = new Intl.NumberFormat(lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: Number.isInteger(min) ? 0 : 2
    });

    return formatter.format(min);
  }

  /**
   * Event "From" price while sale pricing is on, mirroring price.liquid's
   * event branch (cents − truncated pct%). Empty for products that keep their
   * own price: a compare-at markdown, or Alexandre Ligios (never discounted;
   * by vendor or the alexandre-ligios collection, as in price.liquid).
   */
  function formatEventPrice(hit, pct, currency, lang) {
    if (!hit || !(pct > 0)) return '';
    const min = parseFloat(hit.variants_min_price);
    if (Number.isNaN(min)) return '';
    const compareMax = parseFloat(hit.variants_compare_at_price_max);
    if (!Number.isNaN(compareMax) && compareMax > min) return '';
    if (hit.vendor === 'Alexandre Ligios') return '';
    if (Array.isArray(hit.collections) && hit.collections.includes('alexandre-ligios')) return '';

    const cents = Math.round(min * 100);
    const eventCents = cents - Math.floor((cents * pct) / 100);
    const value = eventCents / 100;
    const formatter = new Intl.NumberFormat(lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2
    });
    return formatter.format(value);
  }

  function imageUrl(src, width) {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  /** Shared identity, set up in snippets/algolia-config.liquid. */
  function algoliaUser() {
    return typeof root.rsAlgoliaUser === 'function' ? root.rsAlgoliaUser() : {};
  }

  /**
   * Turns Shopify product ids into the objectIDs Algolia will accept as seeds.
   * Callers hold product ids (cart lines, saved board items, recently viewed)
   * but the index holds one record per product keyed to its first available
   * variant, so the two rarely match. The numeric `id` is the only identifier
   * this index will filter on.
   *
   * @param {{search: Function}} index an initialised Algolia index
   * @returns {Promise<string[]>}
   */
  function resolveSeedObjectIds(index, productIds) {
    const ids = [];
    for (const value of productIds || []) {
      const numeric = parseInt(value, 10);
      if (!Number.isNaN(numeric) && ids.indexOf(numeric) === -1) ids.push(numeric);
    }
    if (!ids.length || !index || typeof index.search !== 'function') return Promise.resolve([]);

    return index
      .search('', {
        // Resolution lookup, not a shopper search — keep it out of Analytics.
        analytics: false,
        filters: ids.map((id) => `id=${id}`).join(' OR '),
        hitsPerPage: ids.length,
        attributesToRetrieve: ['objectID', 'id'],
        ...algoliaUser()
      })
      .then((response) => {
        const hits = (response && response.hits) || [];
        // Preserve caller order — most recent first matters for recency seeds.
        const byProductId = new Map(hits.map((hit) => [String(hit.id), String(hit.objectID)]));
        return ids.map((id) => byProductId.get(String(id))).filter(Boolean);
      })
      .catch(() => []);
  }

  /**
   * Same job as resolveSeedObjectIds, over raw fetch, for callers that have no
   * algoliasearch client (the <recs-engine> element).
   *
   * This is not belt-and-braces. The Shopify connector indexes one variant per
   * product and it is not necessarily the first: marianne-chandelier indexes
   * its 4th variant, while Liquid's selected_or_first_available_variant returns
   * the 1st. Seeding with the Liquid id silently returns nothing, and the
   * carousel quietly falls back to the collection.
   */
  async function lookupObjectIdsByProductId(productIds, options) {
    const opts = options || {};
    const cfg = opts.config || root.algoliaConfig;
    const ids = [];
    for (const value of productIds || []) {
      const numeric = parseInt(value, 10);
      if (!Number.isNaN(numeric) && ids.indexOf(numeric) === -1) ids.push(numeric);
    }
    if (!ids.length || !cfg || !cfg.app_id || !cfg.search_api_key) return [];

    const indexName = opts.indexName || `${cfg.index_prefix || 'shopify_'}products`;
    try {
      const response = await fetch(
        `https://${cfg.app_id}-dsn.algolia.net/1/indexes/${encodeURIComponent(indexName)}/query`,
        {
          method: 'POST',
          headers: {
            'X-Algolia-Application-Id': cfg.app_id,
            'X-Algolia-API-Key': cfg.search_api_key,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: '',
            analytics: false, // a seed lookup, not a shopper search
            hitsPerPage: ids.length,
            filters: ids.map((id) => `id=${id}`).join(' OR '),
            attributesToRetrieve: ['objectID', 'id'],
            ...algoliaUser()
          }),
          signal: opts.signal
        }
      );
      if (!response.ok) return [];
      const data = await response.json();
      const byProductId = new Map((data.hits || []).map((hit) => [String(hit.id), String(hit.objectID)]));
      return ids.map((id) => byProductId.get(String(id))).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  // `id` (el id NUMÉRICO de producto) se agregó a CARD_ATTRIBUTES el 2026-09-09:
  // lo necesitan el corazón de boards y la atribución del quick add, que llega
  // por on:cart:add con el product_id del carrito.
  //
  // >>> NO ASUMIR QUE objectID == primera variante. Lo verifiqué sobre UN
  // producto (9082882097452 → objectID 50619894563116, que sí es su primera
  // variante) y generalicé de más. El comentario de lookupObjectIdsByProductId
  // acá arriba documenta el contraejemplo: el conector de Shopify indexa UNA
  // variante por producto y no necesariamente la primera (marianne-chandelier
  // indexa la 4ta). Por eso `id` se lee del hit y no se deriva del objectID.
  const CARD_ATTRIBUTES = [
    'id',
    'title',
    'handle',
    'product_image',
    'image',
    'variants_min_price',
    'variants_max_price',
    'variants_compare_at_price_max',
    'vendor',
    'collections',
    'tags'
  ];

  /**
   * Asks every model in the chain in one batched Recommend call, then fills in
   * chain order. bought-together is the strongest signal but is limited to real
   * co-purchase pairs — often only two or three — so the next model tops up.
   *
   * Shared by <recs-engine> and the wishlist board grid so both surfaces apply
   * the same models and the same exclusion rules.
   *
   * @returns {Promise<{hits: object[], models: string[]}>}
   */
  async function fetchRecommendations(options) {
    const opts = options || {};
    const cfg = opts.config || root.algoliaConfig;
    const empty = { hits: [], models: [] };
    if (!cfg || !cfg.app_id || !cfg.search_api_key) return empty;

    const models = opts.models || [];
    const seeds = (opts.seeds || []).slice(0, opts.maxSeeds || 3);
    const limit = opts.limit || 8;
    const indexName = opts.indexName || `${cfg.index_prefix || 'shopify_'}products`;
    const excludeHandles = opts.excludeHandles || [];

    const queryParameters = {
      distinct: true,
      // Without this Algolia will not attribute clicks or revenue back to the
      // model that served the item.
      clickAnalytics: true,
      // Same identity the Insights events use, so the request and the events
      // that follow it belong to one person.
      ...algoliaUser(),
      filters: buildFilters(opts.excludeProductIds),
      attributesToRetrieve: opts.attributesToRetrieve || CARD_ATTRIBUTES,
      attributesToHighlight: [],
      attributesToSnippet: []
    };

    const maxRecommendations = Math.min(Math.max(limit, 10) * 2, 30);
    const requests = [];
    const requestModel = [];

    for (const model of models) {
      if (model === 'trending-items') {
        requests.push({ indexName, model, threshold: 0, maxRecommendations, queryParameters });
        requestModel.push(model);
        continue;
      }
      for (const objectID of seeds) {
        requests.push({
          indexName,
          model,
          threshold: 0,
          maxRecommendations,
          objectID: String(objectID),
          queryParameters
        });
        requestModel.push(model);
      }
    }

    if (requests.length === 0) return empty;

    const response = await fetch(`https://${cfg.app_id}-dsn.algolia.net/1/indexes/*/recommendations`, {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': cfg.app_id,
        'X-Algolia-API-Key': cfg.search_api_key,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requests }),
      signal: opts.signal
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return fillFromModels(
      groupResultsByModel(data.results || [], requestModel, models),
      excludeHandles,
      limit
    );
  }

  /* ---------------------------- Insights ----------------------------------
   * Every surface reports through here so one model reads the same in the
   * Algolia dashboard wherever it is rendered.
   *
   * Event names are "Recs <Model> <Action> (<Surface>)" — the model alone is
   * not enough, because Related items serves both the cart and the wishlist.
   * ---------------------------------------------------------------------- */
  const MODEL_LABELS = {
    'bought-together': 'Bought Together',
    'related-products': 'Related',
    'looking-similar': 'Looking Similar',
    'trending-items': 'Trending'
  };
  const SURFACE_LABELS = {
    pdp: 'PDP',
    collection: 'Collection',
    cart: 'Cart',
    wishlist: 'Wishlist',
    home: 'Home',
    search: 'Search Panel'
  };
  const MAX_OBJECT_IDS = 20;

  function recsEventName(action, model, surface) {
    const modelLabel = MODEL_LABELS[model] || 'Recs';
    const surfaceLabel = SURFACE_LABELS[surface] || surface;
    const name = `Recs ${modelLabel} ${action}`;
    return surfaceLabel ? `${name} (${surfaceLabel})` : name;
  }

  function insightsReady() {
    return typeof root.aa === 'function';
  }

  /** Impressions, split per model so CTR is per model rather than per row. */
  function trackRecsView(hits, options) {
    if (!insightsReady() || !hits || !hits.length) return;
    const { indexName, surface } = options || {};

    const byModel = new Map();
    for (const hit of hits) {
      if (!hit || !hit.objectID) continue;
      const list = byModel.get(hit.__model) || [];
      list.push(String(hit.objectID));
      byModel.set(hit.__model, list);
    }

    for (const [model, objectIDs] of byModel) {
      for (let i = 0; i < objectIDs.length; i += MAX_OBJECT_IDS) {
        root.aa('viewedObjectIDs', {
          index: indexName,
          eventName: recsEventName('Viewed', model, surface),
          objectIDs: objectIDs.slice(i, i + MAX_OBJECT_IDS)
        });
      }
    }
  }

  /**
   * Click, plus a note of where it came from.
   *
   * The click is the ONLY event this file sends after an interaction. The
   * conversion — navigate-then-add or quick-add alike — is sent by
   * assets/algolia-insights.js from the `on:cart:add` event, using the
   * attribution stored here by rememberRecsAttribution.
   *
   * Until 2026-09-18 this file also fired its own `convertedObjectIDs*` on
   * `on:cart:add` for the quick-add case. Both listeners ran on the same
   * dispatch, so every quick-add produced two conversions under two names
   * that differed only in casing, and the "forget the attribution after
   * sending" guard could not help — it protected against a *later* second
   * add, not against two writers on one event. Measured on staging. One
   * writer now; it also carries objectData / value / currency, which the
   * `convertedObjectIDs*` path never did.
   */
  function trackRecsClick(hit, options) {
    if (!hit || !hit.objectID) return;
    const { indexName, surface, position } = options || {};
    const name = recsEventName('Clicked', hit.__model, surface);

    if (insightsReady()) {
      if (hit.__queryID) {
        root.aa('clickedObjectIDsAfterSearch', {
          index: indexName,
          eventName: name,
          queryID: hit.__queryID,
          objectIDs: [String(hit.objectID)],
          positions: [position || 1]
        });
      } else {
        root.aa('clickedObjectIDs', {
          index: indexName,
          eventName: name,
          objectIDs: [String(hit.objectID)]
        });
      }
    }

    rememberRecsAttribution(hit, indexName, surface);
  }

  /** Shared, per-objectID attribution map — see snippets/algolia-config.liquid. */
  function rememberRecsAttribution(hit, indexName, surface) {
    if (!root.rsAlgoliaAttribution) return;
    root.rsAlgoliaAttribution.remember({
      objectID: String(hit.objectID),
      queryID: hit.__queryID || '',
      productId: hit.id != null ? String(hit.id) : '',
      indexName: indexName,
      source: 'recs',
      model: hit.__model,
      surface: surface
    });
  }

  root.RSRecsEngine = {
    parseJsonAttr,
    isExcluded,
    mergeHits,
    modelChain,
    groupResultsByModel,
    fillFromModels,
    buildFilters,
    fetchRecommendations,
    resolveSeedObjectIds,
    lookupObjectIdsByProductId,
    formatPrice,
    recsEventName,
    trackRecsView,
    trackRecsClick,
    CARD_ATTRIBUTES
  };

  if (typeof customElements === 'undefined' || customElements.get('recs-engine')) return;

  class RecsEngine extends HTMLElement {
    #controller = null;
    #timer = null;
    #bound = false;
    #hits = [];
    #indexName = '';
    #pickerTemplate = null;
    #quickAddTemplate = null;

    constructor() {
      super();
    }

    connectedCallback() {
      if (this.#bound) return;
      this.#bound = true;
      // Host is display:contents — IntersectionObserver never intersects it.
      // Observe the grid (or parent) which still has a box.
      const target = this.querySelector('[data-recs-grid]') || this.parentElement || this;
      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(target, this.init.bind(this), 500);
      } else {
        setTimeout(() => this.init(), 0);
      }
    }

    disconnectedCallback() {
      this.#clearTimer();
      this.#controller?.abort();
    }

    async init() {
      this.grid = this.querySelector('[data-recs-grid]');
      if (!this.grid) return;

      this.limit = parseInt(this.dataset.limit, 10) || 8;
      const models = modelChain(this.dataset.engine, this.dataset.fallbackEngine);
      if (models.length === 0) return;

      // El corazón y el quick add se toman prestados de las tarjetas que
      // renderizó Liquid, ANTES de pisarlas.
      this.#capturePicker();
      this.#captureQuickAdd();

      this.setAttribute('aria-busy', 'true');
      try {
        await this.#renderAlgolia(models);
      } finally {
        // En el finally y no después del await: si algo tira, la grilla no
        // puede quedar anunciada como "cargando" para siempre.
        this.setAttribute('aria-busy', 'false');
        this.#clearTimer();
      }
    }

    /**
     * Guarda una copia del picker de boards que el tema ya renderizó.
     * DEGRADA BIEN: si no hay ninguno (colección vacía, card sin producto),
     * queda en null y las tarjetas salen sin corazón — que es lo que pasaba
     * antes de este cambio.
     */
    #capturePicker() {
      if (this.#pickerTemplate) return;
      const picker = this.grid.querySelector('[data-rs-board-picker]');
      if (!picker) return;
      const copy = picker.cloneNode(true);
      // El estado "guardado" es del producto de la tarjeta vieja, no de la nueva.
      copy.querySelectorAll('.is-saved').forEach((el) => el.classList.remove('is-saved'));
      const pop = copy.querySelector('.rs-bp__pop');
      if (pop) pop.setAttribute('hidden', '');
      const heart = copy.querySelector('.rs-bp__heart');
      if (heart) heart.setAttribute('aria-expanded', 'false');
      this.#pickerTemplate = copy;
    }

    /**
     * Idem para el quick add. Se clona el contenedor entero (.card__quick-add)
     * porque adentro van el botón y el link "ver producto".
     *
     * >>> SE BORRA `data-quick-add-listener-added`: quick-add.js lo usa como
     * guard para no bindear dos veces el prefetch de hover, y un clon lo
     * arrastraría en "true" — el MutationObserver del drawer lo saltearía y la
     * tarjeta perdería el prefetch. El click NO depende de esto: es delegado.
     */
    #captureQuickAdd() {
      if (this.#quickAddTemplate) return;
      const block = this.grid.querySelector('.card__quick-add');
      if (!block || !block.querySelector('.js-quick-add')) return;
      const copy = block.cloneNode(true);
      copy.querySelectorAll('.js-quick-add').forEach((btn) => {
        delete btn.dataset.quickAddListenerAdded;
        btn.removeAttribute('aria-disabled');
      });
      this.#quickAddTemplate = copy;
    }

    /**
     * @param {string[]} models
     * @returns {Promise<boolean>}
     */
    async #renderAlgolia(models) {
      const cfg = window.algoliaConfig;
      const cardTemplate = this.querySelector('template[data-recs-card]');
      if (!cfg || !cfg.app_id || !cfg.search_api_key || !cardTemplate) return false;

      const indexName = this.dataset.index || `${cfg.index_prefix || 'shopify_'}products`;

      // One signal for the whole run; #signal() aborts any previous one.
      const signal = this.#signal();

      try {
        // Prefer resolving the product id to whichever variant Algolia indexed;
        // the Liquid variant id is not always that one.
        const productIds = parseJsonAttr(this.dataset.seedProductIds);
        const resolved = productIds.length
          ? await lookupObjectIdsByProductId(productIds, { config: cfg, indexName, signal })
          : [];
        const seeds = resolved.length ? resolved : parseJsonAttr(this.dataset.seedObjectIds);
        this.dataset.recsSeedSource = resolved.length ? 'product-id' : 'variant-id';

        const { hits, models: used } = await fetchRecommendations({
          config: cfg,
          indexName,
          models,
          seeds,
          excludeHandles: parseJsonAttr(this.dataset.excludeHandles),
          excludeProductIds: parseJsonAttr(this.dataset.excludeProductIds),
          limit: this.limit,
          signal
        });
        if (hits.length === 0) return false;

        this.#renderCards(hits, cardTemplate, indexName);
        this.dataset.recsResolved = used.join('+');
        this.dataset.recsCount = String(hits.length);
        this.#refreshCarousel();
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return true;
        return false;
      }
    }

    #renderCards(hits, cardTemplate, indexName) {
      const fragment = document.createDocumentFragment();

      for (const hit of hits) {
        const card = cardTemplate.content.cloneNode(true);
        const href = `/products/${hit.handle}`;
        const src = hit.product_image || hit.image;

        card.querySelectorAll('[data-rec-link]').forEach((link) => {
          link.href = href;
        });

        const title = card.querySelector('[data-rec-title]');
        if (title) title.textContent = hit.title || '';

        const price = card.querySelector('[data-rec-price]');
        if (price) {
          const text = formatPrice(
            hit,
            this.dataset.currency || 'USD',
            document.documentElement.lang || 'en'
          );
          const eventText = text ? formatEventPrice(
            hit,
            parseInt(this.dataset.eventPct, 10),
            this.dataset.currency || 'USD',
            document.documentElement.lang || 'en'
          ) : '';
          if (eventText) {
            // Sale event (e.g. Black Friday), same as the Liquid shelves in
            // price.liquid: red event "From" price, regular price struck.
            price.textContent = eventText;
            const regularLabel = document.createElement('span');
            regularLabel.className = 'visually-hidden';
            regularLabel.textContent = this.dataset.regularLabel || '';
            const was = document.createElement('span');
            was.className = 'notranslate ht-money price--from-was';
            was.textContent = text;
            price.after(regularLabel, was);
            const priceBlock = price.closest('.price-block');
            if (priceBlock) priceBlock.classList.add('price--from-event');
          } else if (text) {
            price.textContent = text;
          } else {
            const priceBlock = price.closest('.price-block') || price;
            priceBlock.remove();
          }
        }

        const image = card.querySelector('[data-rec-image]');
        if (image) {
          if (src) {
            image.src = imageUrl(src, 560);
            image.srcset = `${imageUrl(src, 280)} 280w, ${imageUrl(src, 560)} 560w`;
            image.sizes = '(max-width: 767px) 61vw, 280px';
            image.alt = hit.title || '';
          } else {
            image.remove();
          }
        }

        // El corazón de boards. Va dentro de .card__media, que es el único
        // ancestro posicionado — el picker se ubica absolute arriba a la
        // derecha desde rs-boards.css.
        const media = card.querySelector('.card__media');
        if (media && this.#pickerTemplate && hit.id) {
          const picker = this.#pickerTemplate.cloneNode(true);
          picker.dataset.productId = String(hit.id);
          // El objectID del índice es el id de la primera variante disponible.
          picker.dataset.variantId = String(hit.objectID || '');
          picker.dataset.handle = hit.handle || '';
          picker.dataset.title = hit.title || '';
          picker.dataset.image = src ? imageUrl(src, 600) : '';
          media.appendChild(picker);
        }

        // El quick add. Va al final de .card__info-container, que es donde lo
        // pone product-card.liquid.
        const info = card.querySelector('.card__info-container');
        if (info && this.#quickAddTemplate) {
          const quickAdd = this.#quickAddTemplate.cloneNode(true);
          quickAdd.querySelectorAll('.js-quick-add').forEach((btn) => {
            btn.dataset.productUrl = href;
          });
          quickAdd.querySelectorAll('.quick-add-view-btn').forEach((link) => {
            link.href = href;
          });
          info.appendChild(quickAdd);
        }

        const item = card.querySelector('li');
        if (item) item.dataset.recObjectId = hit.objectID;

        fragment.appendChild(card);
      }

      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);
      this.#hits = hits;
      this.#indexName = indexName;
      this.#bindInsights();
      this.#observeImpression();
      // Pinta el estado "guardado" de los corazones recién insertados.
      try {
        root.RSBoards?.reconcileHearts?.();
      } catch (error) {
        /* boards es opcional — la grilla se renderiza igual */
      }
    }

    get #surface() {
      return this.dataset.surface || '';
    }

    #bindInsights() {
      if (this.dataset.insightsBound) return;
      this.dataset.insightsBound = 'true';

      this.grid.addEventListener('click', (evt) => {
        // El corazón guarda en un board, no es un clic de producto: contarlo
        // como click de recomendación ensuciaría el CTR del modelo.
        if (evt.target.closest('[data-rs-board-picker]')) return;

        const item = evt.target.closest('[data-rec-object-id]');
        if (!item) return;

        const objectID = item.dataset.recObjectId;
        const hit = this.#hits.find((candidate) => String(candidate.objectID) === objectID);
        if (!hit) return;

        // The quick-add button sits inside the card. Pressing it is not a
        // recommendation click — counting it inflated CTR with a second
        // click per quick-add (measured 2026-09-18) — but the add that
        // follows still belongs to this recommendation, so remember the
        // attribution for algolia-insights.js without sending a click.
        if (evt.target.closest('.card__quick-add')) {
          rememberRecsAttribution(hit, this.#indexName, this.#surface);
          return;
        }

        trackRecsClick(hit, {
          indexName: this.#indexName,
          surface: this.#surface,
          position: this.#hits.indexOf(hit) + 1
        });
      });
    }

    /** Impression once the row is genuinely on screen, not when it is fetched. */
    #observeImpression() {
      if (this.dataset.impressionSent || typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.disconnect();
            if (this.dataset.impressionSent) return;
            this.dataset.impressionSent = 'true';
            trackRecsView(this.#hits, { indexName: this.#indexName, surface: this.#surface });
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(this.grid);
    }

    /**
     * >>> NO ALCANZA CON REFRESCAR: HAY QUE INICIALIZAR.
     * <carousel-slider> se da de baja si en su connectedCallback el carrusel
     * está vacío, y queda con el atributo `inactive`. En la PDP eso pasa
     * siempre, porque las tarjetas las inyecta este archivo después. Medido:
     * a los 8 segundos, con las 10 tarjetas puestas, el componente seguía
     * `inactive`, `slides` sin definir y las flechas sin hacer nada.
     */
    #refreshCarousel() {
      const carousel = this.closest('carousel-slider');
      if (!carousel) return;

      const items = Array.from(this.grid.querySelectorAll('.slider__item'));
      if (!items.length) return;

      if (carousel.hasAttribute('inactive') && typeof carousel.init === 'function') {
        try {
          carousel.init();
        } catch (error) {
          /* si init() no puede, abajo se intenta el refresco normal */
        }
      }

      if (typeof carousel.initSlider !== 'function') return;
      carousel.slides = items;
      if (typeof carousel.removeListeners === 'function') carousel.removeListeners();
      carousel.initSlider();
    }

    #clearTimer() {
      if (this.#timer) {
        window.clearTimeout(this.#timer);
        this.#timer = null;
      }
    }

    #signal() {
      this.#clearTimer();
      this.#controller?.abort();
      this.#controller = new AbortController();
      // Sin esto un fetch que no contesta nunca deja aria-busy en "true" para
      // siempre, y un lector de pantalla anuncia la grilla como "cargando"
      // sin fin. Medido el 2026-09-09.
      this.#timer = window.setTimeout(() => this.#controller?.abort(), FETCH_TIMEOUT_MS);
      return this.#controller.signal;
    }
  }

  customElements.define('recs-engine', RecsEngine);
})(typeof window !== 'undefined' ? window : globalThis);
